import { twMerge } from 'tailwind-merge';
import { filterOutKeyValue } from '@witmetrics/utils/arrays';
import Breadcrumbs, {
  type BreadcrumbChild,
  Crumb,
} from '@/components/Breadcrumbs';
import Title from '@/components/Title';
import { CaretLeftIcon, FolderIcon } from '@/icons';
import { classes, type FileCollectionPath } from './utils';

export type { FileCollectionPath };

export type FileCollectionCrumbsProps = {
  showHome?: boolean;
  showAtHome?: boolean;
  condensed?: boolean;
  className?: string;
  homeValue?: number | undefined;
  homeLabel?: string;
  crumbs: FileCollectionPath[];
  onSelect: (id: number | undefined) => void;
};

export default function FileCollectionCrumbs({
  showHome = true,
  showAtHome = true,
  condensed = false,
  className,
  homeValue = undefined,
  homeLabel = 'Home',
  crumbs,
  onSelect,
}: FileCollectionCrumbsProps) {
  if (condensed) {
    const current = crumbs[crumbs.length - 1];
    const isAtHome = !current || current.id === homeValue;
    if (isAtHome && !showAtHome) return null;
    const parent = crumbs[crumbs.length - 2];
    const backValue = parent ? parent.id : homeValue;
    return (
      <div className={twMerge(classes.condensedWrapper, className)}>
        {!isAtHome && (
          <button
            className={classes.backButton}
            aria-label="Back"
            onClick={() => onSelect(backValue)}>
            <CaretLeftIcon />
          </button>
        )}
        <FolderIcon className={classes.folderIcon} />
        <Title className={classes.title} size="xs">
          {isAtHome ? homeLabel : current.fileCollection.name}
        </Title>
      </div>
    );
  }
  let crumbArray: BreadcrumbChild<number>[] = [];
  if (showHome) {
    crumbArray.push(<Crumb key="home" label={homeLabel} value={homeValue} />);
  }
  crumbArray.push(
    ...(homeValue ? filterOutKeyValue(crumbs, 'id', homeValue) : crumbs).map(
      (crumb) => (
        <Crumb
          key={crumb.id}
          label={crumb.fileCollection.name}
          value={crumb.id}
        />
      )
    )
  );
  if (crumbArray.length === 1) {
    // Ensures a trailing divider after Home
    crumbArray.push(<Crumb key="empty" label="" value={undefined} />);
  }
  return (
    <Breadcrumbs<number | undefined> className={className} onChange={onSelect}>
      {crumbArray}
    </Breadcrumbs>
  );
}
